import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { getPaymentOrderDetails } from '../services/paymentService';
import '../styles/order-details.css';

/**
 * Order Details Page
 * Displays detailed information about a specific order
 */
const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { wallet } = useWallet();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch order details when component mounts
  useEffect(() => {
    if (wallet.connected) {
      fetchOrderDetails();
    } else {
      setIsLoading(false);
    }
  }, [wallet, orderId]);

  // Fetch order details from API
  const fetchOrderDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getPaymentOrderDetails(
        orderId,
        wallet.address,
        wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1), // Convert network to numeric ID
        wallet.type
      );
      
      if (response.success && response.data) {
        setOrder(response.data);
      } else {
        setError(response.message || 'Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('Failed to load order details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status class for styling
  const getStatusClass = (status) => {
    if (!status) return 'status-unknown';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('complete')) {
      return 'status-success';
    } else if (statusLower.includes('pending') || statusLower.includes('process')) {
      return 'status-pending';
    } else if (statusLower.includes('fail') || statusLower.includes('cancel') || statusLower.includes('reject')) {
      return 'status-failed';
    }
    return 'status-unknown';
  };

  // Format blockchain address for display
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length <= 14) return address;
    
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  // Render wallet disconnected state
  const renderWalletDisconnected = () => (
    <div className="order-details-empty">
      <div className="empty-state-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7L12 3L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 7V17L12 21L20 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Wallet Not Connected</h3>
      <p>Connect your wallet to view order details.</p>
      <div className="order-details-actions">
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/portfolio')}
        >
          Go to Portfolio
        </button>
        <button 
          className="btn" 
          onClick={() => wallet.openWalletModal ? wallet.openWalletModal() : null}
        >
          Connect Wallet
        </button>
      </div>
    </div>
  );

  return (
    <div className="order-details-page">
      <div className="container">
        <header className="order-details-header">
          <button className="back-button" onClick={() => navigate('/portfolio')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Portfolio
          </button>
          
          <h1>Order <span>Details</span></h1>
        </header>
        
        {!wallet.connected ? (
          renderWalletDisconnected()
        ) : isLoading ? (
          <div className="order-details-loading">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        ) : error ? (
          <div className="order-details-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
            <div className="order-details-actions">
              <button className="btn" onClick={fetchOrderDetails}>Try Again</button>
              <button className="btn btn-secondary" onClick={() => navigate('/portfolio')}>
                Go to Portfolio
              </button>
            </div>
          </div>
        ) : !order ? (
          <div className="order-details-empty">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 11H5C3.89543 11 3 11.8954 3 13V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7C7 5.93913 7.42143 4.92172 8.17157 4.17157C8.92172 3.42143 9.93913 3 11 3H13C14.0609 3 15.0783 3.42143 15.8284 4.17157C16.5786 4.92172 17 5.93913 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
            </div>
            <h3>Order Not Found</h3>
            <p>We couldn't find details for this order.</p>
            <button className="btn" onClick={() => navigate('/portfolio')}>
              Go to Portfolio
            </button>
          </div>
        ) : (
          <div className="order-details-content">
            <div className="order-card-large">
              <div className="order-status-header">
                <div className="order-meta">
                  <div className="order-id">Order #{order.order_id || order.id}</div>
                  <div className="order-date">{formatDate(order.created_at)}</div>
                </div>
                <div className={`order-status ${getStatusClass(order.status)}`}>
                  {order.status || 'Unknown'}
                </div>
              </div>
              
              <div className="order-product-details">
                <h3>{order.product_name || 'RWA Device'}</h3>
                <div className="product-price">
                  {formatCurrency(order.amount)}
                </div>
              </div>
              
              <div className="order-info-grid">
                <div className="info-item">
                  <div className="info-label">Payment Network</div>
                  <div className="info-value">{order.selected_network || 'N/A'}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Wallet Address</div>
                  <div className="info-value address">{formatAddress(order.wallet_address)}</div>
                </div>
                
                {order.transaction_id && (
                  <div className="info-item">
                    <div className="info-label">Transaction ID</div>
                    <div className="info-value address">{formatAddress(order.transaction_id)}</div>
                  </div>
                )}
                
                {order.payment_processor && (
                  <div className="info-item">
                    <div className="info-label">Payment Processor</div>
                    <div className="info-value">{order.payment_processor}</div>
                  </div>
                )}
              </div>
              
              {/* Payment status timeline */}
              {order.status_history && order.status_history.length > 0 && (
                <div className="order-timeline">
                  <h4>Order Timeline</h4>
                  <div className="timeline">
                    {order.status_history.map((status, index) => (
                      <div key={index} className={`timeline-item ${getStatusClass(status.status)}`}>
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-status">{status.status}</div>
                          <div className="timeline-date">{formatDate(status.timestamp)}</div>
                          {status.message && <div className="timeline-message">{status.message}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display payment information if available */}
              {order.payment_details && (
                <div className="payment-details">
                  <h4>Payment Details</h4>
                  <div className="payment-info-grid">
                    {Object.entries(order.payment_details).map(([key, value], index) => (
                      <div key={index} className="info-item">
                        <div className="info-label">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                        <div className="info-value">{typeof value === 'object' ? JSON.stringify(value) : value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="order-actions">
                {/* Add transaction link if available */}
                {order.transaction_url && (
                  <a href={order.transaction_url} className="btn" target="_blank" rel="noopener noreferrer">
                    View Transaction
                  </a>
                )}
                
                {/* Show appropriate action buttons based on status */}
                {order.status?.toLowerCase().includes('pending') && (
                  <button className="btn" onClick={fetchOrderDetails}>
                    Refresh Status
                  </button>
                )}
                
                {/* If order is complete and has an asset ID, provide link to the asset */}
                {order.status?.toLowerCase().includes('success') && order.asset_id && (
                  <button className="btn" onClick={() => navigate(`/assets/${order.asset_id}`)}>
                    View Device
                  </button>
                )}
                
                <button className="btn btn-secondary" onClick={() => navigate('/portfolio')}>
                  Back to Portfolio
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailsPage;
